import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getQueryStringValue, setQueryStringValue } from '../../utils/queryString';
import qs from "query-string";

const pageConfig = (window as any).pageConfig || {};

interface PageSettings {
    page: number,
    pageSize: number,
    pagination: boolean,
    placeholder: string,
    filtersHeader: string,
    contentTypesHeader: string,
    tagsHeader: string,
    searchButton: string,
    resetButton: string,
    suggestedSearches: string[] | string,
    noResultsText?: any,
    query?: string,
    searchActive?: boolean,
    totalResults?: number,
    searchBreakpoint?: number,
    enableRecombeeResults?: boolean,
    recombeeResultsCount?: number,
    recommendTagText?: string,
}

const removeQueryParameter = (key: string) => {
    const values = qs.parse(window.location.search);
    delete values[key];
    const newQsValue = qs.stringify(values);
    const newurl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        (newQsValue ? `?${newQsValue}` : '');
    window.history.pushState({ path: newurl }, "", newurl);
};

const initialState: PageSettings = {
    page: 1,
    pagination: true,
    pageSize: pageConfig.perPage || 25,
    noResultsText: pageConfig.noResultsText,
    placeholder: pageConfig.placeholder || '',
    filtersHeader: pageConfig.filtersHeader || '',
    contentTypesHeader: pageConfig.contentTypesHeader || '',
    tagsHeader: pageConfig.tagsHeader || '',
    searchButton: pageConfig.searchButton || '',
    resetButton: pageConfig.resetButton || '',
    suggestedSearches: pageConfig.suggestedSearches || [],
    query: '',
    searchActive: false,
    totalResults: 0,
    searchBreakpoint: pageConfig.searchBreakpoint || 1024,
    enableRecombeeResults: pageConfig.enableRecombeeResults || false,
    recombeeResultsCount: pageConfig.recombeeResultsCount || 3,
    recommendTagText: pageConfig.recommendTagText || 'Recommended',
};

const pageSettingsSlice = createSlice({
    name: 'pageSettings',
    initialState,
    reducers: {
        initializeSettings(state) {
            const queryFromUrl = getQueryStringValue('query');
            const pageFromUrl = getQueryStringValue('page');
            
            if (queryFromUrl && typeof queryFromUrl === 'string') {
                state.query = queryFromUrl;
                state.searchActive = true;
            } else {
                state.searchActive = false;
            }
            
            if (pageFromUrl && typeof pageFromUrl === 'string') {
                const pageNum = parseInt(pageFromUrl);
                if (!isNaN(pageNum) && pageNum > 0) {
                    state.page = pageNum;
                }
            }
        },
        
        setCurrentPage(state, action: PayloadAction<number>) {
            state.page = action.payload;
            
            // Update URL
            if (action.payload > 1) {
                setQueryStringValue('page', action.payload.toString());
            } else {
                removeQueryParameter('page');
            }
        },
        
        setPagination(state, action: PayloadAction<boolean>) {
            state.pagination = action.payload;  
        },
        
        updateLimit(state, action: PayloadAction<number>) {
            state.pageSize = action.payload;
        },
        
        setQuery(state, action: PayloadAction<string>) {
            state.query = action.payload;
            state.searchActive = action.payload.trim() !== '';
            state.page = 1; // Always reset to page 1 when query changes
            

            if (action.payload.trim() !== '') {
                setQueryStringValue('query', action.payload);
            } else {
                removeQueryParameter('query');
            }
            
            // Remove page parameter and filter parameters when setting new query
            removeQueryParameter('page');
            removeQueryParameter('types');
            removeQueryParameter('tags');
        },
        
        setTotalResults(state, action: PayloadAction<number>) {
            state.totalResults = action.payload;
        }
    },
});

export const { 
    setCurrentPage, 
    setPagination, 
    updateLimit, 
    setQuery, 
    initializeSettings,
    setTotalResults 
} = pageSettingsSlice.actions;

export default pageSettingsSlice.reducer;